import React, { useState } from 'react';
import { Box, CircularProgress, Popover, Skeleton, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DosenTooltip from '../ui/DosenTooltip';
import TopPerformersCard from './TopPerformersCard';
import ProgramsDuePanel from './ProgramsDuePanel';
import { useLearnerGamification, LearnerGamificationSummary } from './useLearnerGamification';
import { fadeInSx } from '../ui/DashboardSkeletons';
import { MOBILE_SX } from '../../mobile/mobileApp';

// Learner gamification bar — the low-intensity presentation of the learner
// mechanics. One ~40px row: Continue (the only always-visible action, per
// "resume is the default") + small stat chips whose full content lives in
// on-demand flyouts. Rationale: Eger et al. 2024 (higher element intensity
// hurt retention and autonomy) + user-controlled disclosure. The pillars
// stay the hero of the page.
//
// Cian v2: Continue copy = "Continue: [Program Name: Lesson Name]" with
// course completion % + estimated time remaining (estimate source TBD);
// Programs Due chip (red !) right of the fire icon opens a modal sorted
// soonest first; the medal chip opens the Medals popup.
//
// Real data or nothing. Every piece here renders only from the host's resume
// target and the live summary endpoint; with neither, the bar renders nothing
// at all rather than an empty frame. The demo fallbacks this used to carry
// (previewData) reached real learners once the dashboard went GA — a learner
// with zero programs was shown another org's program at 50% complete.

const SURFACE = '#1C1C1C';
const BORDER_DIM = '#2A2A2A';
const TEXT_PRIMARY = '#FDFDFD';
const TEXT_SECONDARY = '#B6B6B6';
const BRAND_LIGHT = '#F79420';
const DUE_RED = '#DA2828';
const BRANDY_GRADIENT = 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)';
const TRACK = '#2A2A2A';

const chipSx = {
  // Each chip appears once its data loads; ease it in rather than popping.
  ...fadeInSx,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  height: 32,
  px: '12px',
  borderRadius: '999px',
  bgcolor: SURFACE,
  border: `1px solid ${BORDER_DIM}`,
  cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
  fontSize: 12.5,
  color: TEXT_SECONDARY,
  whiteSpace: 'nowrap',
  transition: 'border-color 0.15s ease',
  '&:hover': { borderColor: '#565656' },
  [MOBILE_SX]: { height: 36, px: '14px', fontSize: 14 }
} as const;

// Shared flyout title: brandy eyebrow with the panel's own icon on the left.
// Every panel (Streak, Top Performers, Programs Due, Tier) uses this so the
// headings read as one family.
const panelTitleSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontFamily: 'Inter, sans-serif',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '1.2px',
  color: BRAND_LIGHT,
  lineHeight: 1,
  whiteSpace: 'nowrap'
} as const;

const popoverPaperSx = {
  bgcolor: 'transparent',
  boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
  borderRadius: '8px',
  mt: '10px',
  // The pointer sits above the panel's top edge — don't clip it.
  overflow: 'visible'
} as const;

type FlyoutKey = 'streak' | 'top' | 'due';

// Caret at the top of a flyout, aimed back at the chip that opened it, so
// every panel visibly belongs to its icon.
const PanelPointer: React.FC<{ color?: string }> = ({ color = BORDER_DIM }) => (
  <Box
    sx={{
      position: 'absolute',
      top: -6,
      right: 20,
      width: 0,
      height: 0,
      borderLeft: '6px solid transparent',
      borderRight: '6px solid transparent',
      borderBottom: `6px solid ${color}`
    }}
  />
);

export interface ResumeTarget {
  /** The Continue pill's label: the next lesson's name, or the program's when no lesson is known. */
  label: string;
  /** On hover: the program and the lesson, in full. */
  tooltip?: string;
  progressPct: number | null;
  timeRemainingMinutes: number | null;
  onContinue: () => void;
}

interface Props {
  /** Real resume target from the dashboard's program data. When absent there
      is no Continue pill, no gauge and no time remaining — a learner with no
      programs must never be told to continue one. */
  resumeTarget?: ResumeTarget | null;
  /** Summary lifted by the host page (the LL dashboard shares one fetch
      between this bar and the Development Tier panel). Pass `undefined` — the
      default — to let the bar fetch its own. */
  summary?: LearnerGamificationSummary | null;
  /** True while the host dashboard is still fetching its programs. The bar
      then shows a neutral skeleton in the same frame, so the row doesn't
      collapse and reflow once the real resume target resolves. Gated on the
      host's fetch only: the stat chips already hide themselves while the
      summary loads, so waiting on that too would only blank a real
      Continue pill. */
  loading?: boolean;
}

// Outer panel styling — shared by the loading skeleton and the loaded bar so
// the frame never shifts as data resolves.
const panelSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  minHeight: 36,
  // One row of stats, always — see AdminGamificationBar. Everything holds
  // its size except the Continue pill, which truncates instead. The bar
  // itself stays shrinkable so that pill is what gives; pinning it to
  // max-content here would take that away and push the row's right-hand
  // cluster onto a line of its own.
  flexWrap: 'nowrap',
  minWidth: 0,
  '& > *': { flexShrink: 0 },
  px: '14px',
  py: '15px',
  borderRadius: '12px',
  border: '1px solid rgba(247,148,32,0.35)',
  bgcolor: 'rgba(247,148,32,0.04)',
  boxShadow: '0 0 16px rgba(247,148,32,0.10)',
  // Phone: the summary card. Continue takes a row of its own; the gauge and
  // the stat chips share the row under it.
  [MOBILE_SX]: { flexWrap: 'wrap', rowGap: '12px', columnGap: '10px', p: '14px', borderRadius: '14px' }
} as const;

const LearnerGamificationBar: React.FC<Props> = ({ resumeTarget, summary: providedSummary, loading = false }) => {
  const [flyout, setFlyout] = useState<{ key: FlyoutKey; anchor: HTMLElement } | null>(null);

  // Live data (BE summary endpoint). No fallbacks anywhere: every chip hides
  // while loading or on error rather than showing invented numbers
  // (placeholder data shipped as real data once — RDP, Sep 2026).
  const hostOwnsSummary = providedSummary !== undefined;
  const { summary: ownSummary } = useLearnerGamification(undefined, { skip: hostOwnsSummary });
  const summary = hostOwnsSummary ? providedSummary : ownSummary;
  const streak = summary?.streak ?? null;
  const topPerformers = summary?.topPerformers ?? null;
  const programsDue = summary?.programsDue ?? [];

  // Course gauge inputs — only ever the host's real resume target. A null
  // progress figure means the dashboard has a program but no percentage for
  // it, so the gauge stays out too; `timeRemaining` is null when the estimate
  // isn't known.
  const progressPct = resumeTarget?.progressPct ?? null;
  const timeRemaining = resumeTarget?.timeRemainingMinutes != null ? `${resumeTarget.timeRemainingMinutes} min` : null;

  const openFlyout = (key: FlyoutKey) => (e: React.MouseEvent<HTMLElement>) =>
    setFlyout({ key, anchor: e.currentTarget });
  const close = () => setFlyout(null);

  // Nothing real to say — no resume target and no live stats — so the bar
  // renders nothing at all. An empty outlined frame in the dashboard header
  // reads as broken; its absence reads as "this learner has no activity yet",
  // which is the truth.
  const hasStats = Boolean(streak) || Boolean(topPerformers) || programsDue.length > 0;
  const hasAnything = Boolean(resumeTarget) || hasStats;

  // Neutral loading state — until the host's programs resolve, show a skeleton
  // in the same frame so the header row doesn't shift when data lands.
  if (loading) {
    return (
      <Box sx={panelSx} aria-busy="true">
        <Skeleton
          variant="rounded"
          width={210}
          height={34}
          sx={{ borderRadius: '999px', bgcolor: 'rgba(247,148,32,0.12)', [MOBILE_SX]: { width: '100%', height: 44 } }}
        />
        <Skeleton variant="rounded" width={96} height={16} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
        <Skeleton variant="rounded" width={78} height={16} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
        <Skeleton variant="rounded" width={54} height={22} sx={{ ml: 'auto', bgcolor: 'rgba(255,255,255,0.06)' }} />
      </Box>
    );
  }

  if (!hasAnything) return null;

  return (
    // Outlined panel: the section is the first thing a learner should land on,
    // so it's framed rather than floating loose in the header row. No margin or
    // trailing spacer — it sits as one cluster in the dashboard's header row.
    <Box sx={panelSx}>
      {/* Resume — the one always-visible action, and only ever the learner's
          own next lesson. No resume target (a learner with no programs, or
          none in progress) means no pill, no gauge and no time remaining:
          there is nothing truthful to put there. */}
      {resumeTarget && (
        <>
          <DosenTooltip title={resumeTarget.tooltip ?? ''}>
            <Box
              onClick={resumeTarget.onContinue}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                height: 34,
                px: '16px',
                borderRadius: '999px',
                cursor: 'pointer',
                color: TEXT_PRIMARY,
                fontFamily: 'Inter, sans-serif',
                fontSize: 13,
                fontWeight: 600,
                background: BRANDY_GRADIENT,
                whiteSpace: 'nowrap',
                // As wide as the lesson name (the host caps that at 50 characters). Still
                // the only item in the bar allowed to give: on a screen too narrow for the
                // whole row, minWidth 0 lets it shrink past its text so the ellipsis
                // below engages, instead of the header wrapping onto a second line.
                flexShrink: 1,
                minWidth: 0,
                overflow: 'hidden',
                transition: 'filter 0.15s ease',
                '&:hover': { filter: 'brightness(1.08)' },
                // Phone: a row of its own, and a long lesson name wraps instead of cutting off.
                [MOBILE_SX]: {
                  flex: '1 0 100%',
                  justifyContent: 'center',
                  height: 'auto',
                  minHeight: 44,
                  py: '10px',
                  fontSize: 15,
                  lineHeight: 1.3,
                  textAlign: 'center',
                  whiteSpace: 'normal',
                  borderRadius: '22px',
                  '& > span': { overflow: 'visible' }
                }
              }}
            >
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Continue: {resumeTarget.label}
              </Box>
            </Box>
          </DosenTooltip>

          {/* Course gauge — completion % and estimated time remaining are two
              separate facts (Cian v2; estimate source is an open decision), so
              they read as two items rather than one run-on line. Each shows
              only when the dashboard actually has the figure. */}
          {progressPct != null && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
              <Box sx={{ position: 'relative', width: 20, height: 20 }}>
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={20}
                  thickness={5}
                  sx={{ color: TRACK, position: 'absolute', inset: 0 }}
                />
                <CircularProgress
                  variant="determinate"
                  value={progressPct}
                  size={20}
                  thickness={5}
                  sx={{ color: '#F0B80D', position: 'absolute', inset: 0 }}
                />
              </Box>
              <Typography
                sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEXT_SECONDARY, whiteSpace: 'nowrap' }}
              >
                <Box component="span" sx={{ fontWeight: 700, color: TEXT_PRIMARY }}>
                  {progressPct}%
                </Box>
              </Typography>
            </Box>
          )}

          {timeRemaining && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', ml: '6px' }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: TEXT_SECONDARY }} />
              <Typography
                sx={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: TEXT_SECONDARY, whiteSpace: 'nowrap' }}
              >
                <Box component="span" sx={{ fontWeight: 700, color: TEXT_PRIMARY }}>
                  {timeRemaining}
                </Box>{' '}
                remaining
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Resume + course gauge above are one sub-section; this hairline splits
          them from the stat chips that follow. Same brandy tint and glow as
          the panel outline so the two read as one system. Only drawn when
          there is something on both sides of it. */}
      {resumeTarget && hasStats && (
        <Box
          sx={{
            width: '1px',
            alignSelf: 'stretch',
            minHeight: 24,
            mx: '8px',
            bgcolor: 'rgba(247,148,32,0.35)',
            boxShadow: '0 0 8px rgba(247,148,32,0.45)',
            // Phone: the two groups sit on one row, so the hairline becomes the
            // space that pushes the chips to the right edge.
            [MOBILE_SX]: {
              flex: '1 1 auto',
              width: 'auto',
              minHeight: 0,
              mx: 0,
              bgcolor: 'transparent',
              boxShadow: 'none'
            }
          }}
        />
      )}

      {/* Stat chips — full detail on demand, in order: Streak, Medal, Due,
          Tier. Tier is last and carries the headline treatment. Each chip
          appears once its live data has loaded. */}
      {streak && (
        <Box onClick={openFlyout('streak')} sx={chipSx}>
          <span aria-hidden>🔥</span>
          <Box component="span" sx={{ fontWeight: 700, color: BRAND_LIGHT }}>
            {streak.weeksActive}
          </Box>
        </Box>
      )}

      {/* Medal — leaderboard position (top performers flyout). */}
      {topPerformers && (
        <Box onClick={openFlyout('top')} sx={chipSx}>
          <span aria-hidden>🏅</span>
          <Box component="span" sx={{ fontWeight: 700, color: TEXT_PRIMARY }}>
            #{topPerformers.thisWeek.you.rank}
          </Box>
        </Box>
      )}

      {/* Programs Due — red exclamation (Cian v2). */}
      {programsDue.length > 0 && (
        <Box onClick={openFlyout('due')} sx={{ ...chipSx, borderColor: 'rgba(218,40,40,0.5)' }}>
          <Box
            component="span"
            sx={{
              width: 15,
              height: 15,
              borderRadius: '50%',
              bgcolor: DUE_RED,
              color: '#FDFDFD',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 800,
              lineHeight: 1
            }}
          >
            !
          </Box>
          <Box component="span" sx={{ fontWeight: 700, color: TEXT_PRIMARY }}>
            {programsDue.length}
          </Box>
        </Box>
      )}

      {/* The tier chip used to sit here. It moved out of the bar entirely: the
          Development Tier panel down the left of the dashboard now owns the
          tier and every measure inside it, so a chip here only said the same
          thing twice. */}

      {/* The spaced-retrieval Refresher chip used to sit here, rendered from a
          hardcoded "3 questions · 2 min". It is gone with the rest of the demo
          data: it comes back with the BE refresher endpoints that tell us what
          is actually due for this learner. */}

      {/* Elliott lives in the floating corner orb (doc §1.8) — see ElliottOrb. */}

      {/* Flyouts */}
      <Popover
        open={flyout !== null}
        anchorEl={flyout?.anchor ?? null}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: popoverPaperSx }}
      >
        {flyout?.key === 'streak' && streak && (
          // Sized to its content so neither line ever wraps.
          <Box
            sx={{
              position: 'relative',
              width: 'max-content',
              maxWidth: 'calc(100vw - 48px)',
              bgcolor: SURFACE,
              border: `1px solid ${BORDER_DIM}`,
              borderRadius: '8px',
              p: '14px 16px'
            }}
          >
            <PanelPointer />
            <Typography sx={panelTitleSx}>
              <span aria-hidden>🔥</span>
              WEEK {streak.weeksActive} ACTIVE
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 12.5,
                color: TEXT_SECONDARY,
                mt: '4px',
                whiteSpace: 'nowrap',
                [MOBILE_SX]: { whiteSpace: 'normal' }
              }}
            >
              {streak.lessonsThisWeek === 0
                ? 'No lessons yet this week. Complete one lesson to keep your streak going.'
                : `${streak.lessonsThisWeek} ${
                    streak.lessonsThisWeek === 1 ? 'lesson' : 'lessons'
                  } this week. Your week is alive.`}
            </Typography>
          </Box>
        )}
        {flyout?.key === 'top' && topPerformers && (
          // max-content so the card can grow to keep its Top-3 line on one row.
          <Box
            sx={{
              position: 'relative',
              width: 'max-content',
              minWidth: 360,
              maxWidth: 'calc(100vw - 48px)',
              [MOBILE_SX]: { minWidth: 0, width: 'calc(100vw - 32px)', maxWidth: 'none' }
            }}
          >
            <PanelPointer />
            <TopPerformersCard data={topPerformers} />
          </Box>
        )}

        {/* Programs Due — soonest first, urgent ones boxed. Shared with the
            admin bar so both dashboards read identically. */}
        {flyout?.key === 'due' && <ProgramsDuePanel items={programsDue} />}
      </Popover>
    </Box>
  );
};

export default LearnerGamificationBar;
